import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - The HTML string to sanitize
 * @param {object} options - Optional DOMPurify configuration
 * @returns {string} Sanitized HTML string
 */
export const sanitizeHtml = (html, options = {}) => {
  if (!html) return '';
  
  // Default configuration - allow common HTML elements and attributes
  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
      'a', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'hr', 'sup', 'sub'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'id',
      'style', 'width', 'height', 'colspan', 'rowspan'
    ],
    ALLOW_DATA_ATTR: false,
    ...options
  };

  return DOMPurify.sanitize(html, defaultConfig);
};

/**
 * Sanitize SVG content (more permissive for SVG elements)
 * @param {string} svg - The SVG string to sanitize
 * @returns {string} Sanitized SVG string
 */
export const sanitizeSvg = (svg) => {
  if (!svg) return '';
  
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'],
    ADD_ATTR: ['xlink:href', 'xmlns', 'xmlns:xlink', 'viewBox', 'fill', 'stroke', 'stroke-width', 'd', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'points', 'transform']
  });
};

/**
 * Create a safe innerHTML object for React's dangerouslySetInnerHTML
 * @param {string} html - The HTML string to sanitize
 * @param {object} options - Optional DOMPurify configuration
 * @returns {object} Object with __html property containing sanitized HTML
 */
export const createSafeHtml = (html, options = {}) => {
  return { __html: sanitizeHtml(html, options) };
};

/**
 * Create a safe SVG innerHTML object for React's dangerouslySetInnerHTML
 * @param {string} svg - The SVG string to sanitize
 * @returns {object} Object with __html property containing sanitized SVG
 */
export const createSafeSvg = (svg) => {
  return { __html: sanitizeSvg(svg) };
};

export default { sanitizeHtml, sanitizeSvg, createSafeHtml, createSafeSvg };
